import { onUnmounted, ref, type Ref } from 'vue';

export interface AsyncDataState<T> {
  data: Ref<T | null>;
  loading: Ref<boolean>;
  error: Ref<Error | null>;
  reload: () => Promise<void>;
}

export interface AsyncDataOptions {
  /** Clear the previous response before starting a replacement request. */
  clearOnReload?: boolean;
}

/** Runs an abortable request and exposes its state to the view. */
export function useAsyncData<T>(fetcher: (signal: AbortSignal) => Promise<T>, options: AsyncDataOptions = {}): AsyncDataState<T> {
  const data = ref<T | null>(null) as Ref<T | null>;
  const loading = ref(false);
  const error = ref<Error | null>(null);
  let controller: AbortController | null = null;
  let requestId = 0;

  async function reload(): Promise<void> {
    controller?.abort();
    const current = new AbortController();
    const currentRequestId = ++requestId;
    controller = current;
    if (options.clearOnReload) data.value = null;
    loading.value = true;
    error.value = null;
    try {
      const result = await fetcher(current.signal);
      if (current.signal.aborted || controller !== current || requestId !== currentRequestId) return;
      data.value = result;
    } catch (reason) {
      if (current.signal.aborted || controller !== current || requestId !== currentRequestId) return;
      error.value = reason instanceof Error ? reason : new Error(String(reason));
    } finally {
      if (controller === current && requestId === currentRequestId) {
        loading.value = false;
        controller = null;
      }
    }
  }

  reload();
  onUnmounted(() => controller?.abort());
  return { data, loading, error, reload };
}
